import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import CommonTable from 'components/commonTable/CommonTable';
import React, { useMemo } from 'react';
import { weekDays } from 'utils/StaticData';
import PropTypes from 'prop-types';
import { formatDate } from 'utils/CommonFunction';
import { formatTimeForDisplay } from 'utils/openingHoursValidation';

const OpeningHoursTable = ({ tableOpeningHoursData, isLoading }) => {
  const textColor = useColorModeValue('primary', 'white');

  const sortedData = useMemo(() => {
    if (!tableOpeningHoursData?.length) {
      return [];
    }

    // Check if the data is for temporary hours or weekly hours
    const isTemporaryHours = tableOpeningHoursData.some((item) => item.date);

    if (isTemporaryHours) {
      return tableOpeningHoursData.map((item) => ({
        date: item.date,
        isClosed: item.isClosed,
        openTime: item.openTime,
        closeTime: item.closeTime,
        remark: item.remark,
      }));
    } else {
      const periodsMap = new Map();
      tableOpeningHoursData.forEach((item) => {
        if (!periodsMap.has(item.dayOfWeek)) {
          periodsMap.set(item.dayOfWeek, []);
        }
        periodsMap.get(item.dayOfWeek).push({
          openTime: item.openTime,
          closeTime: item.closeTime,
        });
      });

      return Array.from({ length: 7 }, (_, dayOfWeek) => {
        const periods = periodsMap.get(dayOfWeek) || [];
        if (periods.length === 0) {
          return { dayOfWeek, openTime: 'Closed', closeTime: 'Closed' };
        }
        const formattedPeriods = periods
          .map((period) => {
            const isClosed =
              period.openTime === '00:00:00' && period.closeTime === '00:00:00';
            if (isClosed) {
              return 'Closed';
            }
            // Extract time in HH:MM format (remove seconds)
            const openTime = period.openTime?.substring(0, 5) || '';
            const closeTime = period.closeTime?.substring(0, 5) || '';

            // Format in 12-hour format for display
            return `${formatTimeForDisplay(openTime)} - ${formatTimeForDisplay(closeTime)}`;
          })
          .join(', ');
        return {
          dayOfWeek,
          openTime: formattedPeriods,
          closeTime: '',
        };
      });
    }
  }, [tableOpeningHoursData]);

  const columnHelper = createColumnHelper();
  const isTemporaryHours = useMemo(
    () => tableOpeningHoursData.some((item) => item.date),
    [tableOpeningHoursData],
  );

  const columns = [
    columnHelper.accessor(
      (row) => (row.date ? formatDate(row.date) : weekDays[row.dayOfWeek]),
      {
        id: 'dayOrDate',
        header: `${isTemporaryHours ? 'Date' : 'Day'}`,
        cell: (info) => (
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        ),
      },
    ),

    columnHelper.accessor('openTime', {
      id: 'times',
      header: 'Opening Hours',
      cell: (info) => {
        const row = info.row.original;
        if (row.isClosed) {
          return (
            <Text color={textColor} fontSize="sm" fontWeight="600">
              Closed
            </Text>
          );
        }
        if (Array.isArray(row.openTime)) {
          return (
            <Flex direction="column">
              {row.openTime.map((ot, i) => {
                const openTime = ot?.substring(0, 5) || '';
                const closeTime = row.closeTime[i]?.substring(0, 5) || '';
                return (
                  <Text
                    key={i}
                    color={textColor}
                    fontSize="sm"
                    fontWeight="600"
                  >
                    {formatTimeForDisplay(openTime)} -{' '}
                    {formatTimeForDisplay(closeTime)}
                  </Text>
                );
              })}
            </Flex>
          );
        }
        return (
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        );
      },
    }),
    ...(isTemporaryHours
      ? [
          columnHelper.accessor('remark', {
            id: 'remark',
            header: 'Remark',
            cell: (info) => (
              <Text color={textColor} fontSize="sm" fontWeight="600">
                {info.getValue()}
              </Text>
            ),
          }),
        ]
      : []),
  ];

  return (
    <>
      <Box pt="4px">
        <CommonTable
          columns={columns}
          data={sortedData}
          isLoading={isLoading}
        />
      </Box>
    </>
  );
};
OpeningHoursTable.propTypes = {
  tableOpeningHoursData: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default OpeningHoursTable;
